import Button from "@/components/Button"
import TextInput from "@/components/forms/TextInput"
import Modal from "@/components/modals/Modal"
import { ModalBody, ModalContent, ModalFooter } from "@nextui-org/modal"
import { ReactComponent as CloudIcon } from "@/icons/cloud-icon.svg"
import { ErrorMessage, Field, Form, Formik } from "formik"
import * as yup from "yup"
import cx from "classnames"
import { ReactComponent as StarIcon } from "@/icons/star-icon.svg"
import { useEffect, useRef, useState } from "react"

type Props = {
  isOpen: boolean
  onClose: () => void
  onConfirm: (name: string) => void
  presets: any[]
  currentEqState: any
  mode: string
  onPresetSelected: (preset: any) => void
  activePreset: any
}

const SaveToDeviceModal = ({
  isOpen,
  onClose,
  onConfirm,
  presets,
  currentEqState,
  mode,
  onPresetSelected,
  activePreset,
}: Props) => {
  const formikRef = useRef(null)
  const [selectedOption, setSelectedOption] = useState<"current" | "preset">(
    activePreset ? "preset" : "current"
  )

  const modePresets = presets
    ? presets.filter((preset) => preset.mode.toLowerCase() === mode.toLowerCase())
    : []

  // Update form values when activePreset changes
  useEffect(() => {
    if (formikRef.current && activePreset) {
      formikRef.current.setFieldValue("name", activePreset.name.substring(0, 22))
    }
  }, [activePreset])

  const handlePresetSelected = (preset: any) => {
    setSelectedOption("preset")
    onPresetSelected(preset)
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalContent>
        {(onClose) => (
          <Formik
            innerRef={formikRef}
            initialValues={{ name: "" }}
            validationSchema={yup.object({
              name: yup
                .string()
                .min(3)
                .max(16)
                .required("Name is required")
                .matches(
                  /^[A-Za-z0-9\s\-_.,!?()&$#@%*+=<>:;'"\/\\]+$/,
                  "Allowed characters: A-Z, 0-9, space, and -_.,!?()&$#@%*+=<>:;'\"\\/"
                ),
            })}
            onSubmit={(values) => {
              onConfirm(values.name)
            }}
          >
            {({ errors, touched, isValid, dirty }) => (
              <Form>
                <ModalBody>
                  <h2 className="type-body-xl font-bold text-white">Store on Element IV</h2>

                  <div className="relative mt-4">
                    <Field
                      as={TextInput}
                      theme="modal"
                      name="name"
                      type="text"
                      autoFocus={true}
                      placeholder="Type a preset name"
                      isInvalid={errors.name && touched.name}
                    />
                    <ErrorMessage
                      component="div"
                      className="type-body-xs absolute -top-2 right-0 -translate-y-full text-right italic text-red"
                      name="name"
                    />
                  </div>

                  <div className="mt-6">
                    <h3 className="type-heading-xs flex items-center gap-x-3 font-bold text-earth">
                      <CloudIcon className="h-3 w-3" />
                      <span className="inline-block pt-0.75">What would you like to store?</span>
                    </h3>
                    <div className="mt-2">
                      <button
                        type="button"
                        onClick={() => setSelectedOption("current")}
                        className={cx(
                          "type-body-xs flex h-8 w-full items-center justify-between rounded-plus border pl-3 pr-2 font-medium transition-colors duration-200",
                          selectedOption === "current"
                            ? "border-[#4B5E47] bg-[#384B32]"
                            : "border-jaguar bg-dark-jaguar hover:border-silver"
                        )}
                      >
                        <div className="flex items-center">Current EQ Settings</div>
                        {selectedOption === "current" && (
                          <div className="pt-px font-pfdin text-[8px] font-medium uppercase tracking-widest2x text-[#71A96C]">
                            Active
                          </div>
                        )}
                      </button>
                    </div>
                  </div>

                  <div className="mt-6">
                    <h3 className="type-heading-xs flex items-center gap-x-3 font-bold text-earth">
                      <CloudIcon className="h-3 w-3" />
                      <span className="inline-block pt-0.75">Available Cloud Presets</span>
                    </h3>
                    <div className="mt-2 max-h-[200px] overflow-y-auto [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-jaguar hover:[&::-webkit-scrollbar-thumb]:bg-silver [&::-webkit-scrollbar-track]:bg-dark-jaguar/40 [&::-webkit-scrollbar]:w-2">
                      <ul className="space-y-1.5">
                        {modePresets.length === 0 && (
                          <p className="text-sm font-normal text-silver">No presets saved</p>
                        )}
                        {modePresets.map((preset, index) => {
                          return (
                            <li key={index}>
                              <button
                                type="button"
                                className={cx(
                                  "type-body-xs flex h-8 w-full items-center justify-between rounded-plus border pl-3 pr-2 font-medium transition-colors duration-200",
                                  selectedOption === "preset" && preset.id === activePreset?.id
                                    ? "border-[#4B5E47] bg-[#384B32]"
                                    : "border-jaguar bg-dark-jaguar hover:border-silver"
                                )}
                                onClick={() => handlePresetSelected(preset)}
                              >
                                <div className="flex items-center">
                                  {preset.is_favourite && (
                                    <StarIcon className="mr-2.5 h-3 w-3 [&>path]:fill-yellow [&>path]:stroke-none" />
                                  )}
                                  {preset.name}
                                </div>
                                <div className="flex items-center gap-x-3">
                                  {selectedOption === "preset" &&
                                    preset.id === activePreset?.id && (
                                      <div className="pt-px font-pfdin text-[8px] font-medium uppercase tracking-widest2x text-[#71A96C]">
                                        Active
                                      </div>
                                    )}
                                </div>
                              </button>
                            </li>
                          )
                        })}
                      </ul>
                    </div>
                  </div>
                </ModalBody>
                <ModalFooter className="gap-x-0">
                  <Button
                    theme="blue"
                    size="sm"
                    outline={true}
                    solid={false}
                    className=""
                    full={false}
                    type="submit"
                    disabled={!isValid || !dirty}
                  >
                    Save to Device
                  </Button>
                  <Button
                    className="-mr-5"
                    theme="none"
                    size="sm"
                    outline={false}
                    solid={true}
                    full={false}
                    onClick={onClose}
                  >
                    Cancel
                  </Button>
                </ModalFooter>
              </Form>
            )}
          </Formik>
        )}
      </ModalContent>
    </Modal>
  )
}

export default SaveToDeviceModal
